/**
 * IncentiveBucketCard — SA / SM view (iter161)
 * ----------------------------------------------------------------
 * Per-mechanic incentive bucket rollup + workshop-wide summary.
 *
 * Formula (approved by user):
 *   daily_delta = section_completed_hours − user.daily_hours
 *   bucket_total = Σ daily_delta since reset_date
 *
 * • Reset date: SA-configurable (defaults to current calendar quarter
 *   start). SA can change it here via a small pen icon.
 * • Column strategy: standalone card next to the existing
 *   MechanicProductivityAutoCard so the reader can compare hourly
 *   productivity vs incentive balance side-by-side.
 *
 * Reads:
 *   GET /api/super-admin/incentive-summary
 *   GET /api/workshop-settings/incentive-reset-date
 * Writes:
 *   PUT /api/workshop-settings/incentive-reset-date  (SA only)
 */
import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Award, TrendingUp, TrendingDown, Users, RefreshCw, Pencil, Save, X,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtSignedHours = (h) => {
  const n = Number(h) || 0;
  const sign = n > 0.05 ? '+' : n < -0.05 ? '−' : '';
  const abs = Math.abs(n).toFixed(1);
  return `${sign}${abs}h`;
};

export default function IncentiveBucketCard({ userRole = 'super_admin' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingReset, setEditingReset] = useState(false);
  const [resetDraft, setResetDraft] = useState('');
  const [savingReset, setSavingReset] = useState(false);

  const canEditReset = ['super_admin', 'admin'].includes(userRole);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/super-admin/incentive-summary`,
        { credentials: 'include' },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setData(j);
      setResetDraft(j.reset_date || '');
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveResetDate = async () => {
    setSavingReset(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/workshop-settings/incentive-reset-date`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ incentive_reset_date: resetDraft || null }),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setEditingReset(false);
      await load();
    } catch (_e) {
      setSavingReset(false);
    } finally {
      setSavingReset(false);
    }
  };

  const clearResetDate = async () => {
    setResetDraft('');
    setSavingReset(true);
    try {
      await fetch(
        `${BACKEND_URL}/api/workshop-settings/incentive-reset-date`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ incentive_reset_date: null }),
        },
      );
      setEditingReset(false);
      await load();
    } catch (_e) { /* silent */ } finally {
      setSavingReset(false);
    }
  };

  if (loading && !data) {
    return (
      <Card className="p-6" data-testid="incentive-bucket-card-loading">
        <p className="text-sm text-slate-500">Loading incentive buckets…</p>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6" data-testid="incentive-bucket-card-error">
        <p className="text-sm text-rose-600">Failed to load: {error}</p>
        <Button variant="outline" size="sm" className="mt-2" onClick={load}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Retry
        </Button>
      </Card>
    );
  }

  const workshopTotal = Number(data?.workshop_total_bucket_hours || 0);
  const workshopTone =
    workshopTotal > 0.05 ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
    : workshopTotal < -0.05 ? 'border-rose-300 bg-rose-50 text-rose-800'
    : 'border-slate-200 bg-slate-50 text-slate-700';

  return (
    <Card className="p-6" data-testid="incentive-bucket-card">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Award className="h-5 w-5 text-amber-500" />
            Incentive Buckets
          </h3>
          <p className="text-sm text-slate-500">
            Running balance of <span className="font-medium">completed − daily target</span>{' '}
            since <span className="font-semibold text-slate-700">{data?.reset_date}</span>.
            Positive hours accrue toward mechanic bonuses; negatives eat into it.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="incentive-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Reset date editor — SA only */}
      <div className="flex items-center gap-3 mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50/40">
        <span className="text-[11px] uppercase tracking-wider font-bold text-amber-900">
          Reset date
        </span>
        {editingReset ? (
          <>
            <Input
              type="date"
              value={resetDraft}
              onChange={(e) => setResetDraft(e.target.value)}
              className="h-8 w-40"
              data-testid="incentive-reset-input"
            />
            <Button
              size="sm" variant="outline"
              onClick={saveResetDate} disabled={savingReset}
              data-testid="incentive-reset-save"
            >
              <Save className="h-3 w-3 mr-1" /> Save
            </Button>
            <Button
              size="sm" variant="ghost"
              onClick={() => { setEditingReset(false); setResetDraft(data?.reset_date || ''); }}
            >
              <X className="h-3 w-3" />
            </Button>
            <Button
              size="sm" variant="ghost"
              onClick={clearResetDate}
              className="text-slate-500"
              title="Reset to current calendar quarter start"
            >
              Use quarter default
            </Button>
          </>
        ) : (
          <>
            <span className="font-semibold text-slate-800 tabular-nums" data-testid="incentive-reset-display">
              {data?.reset_date}
            </span>
            {canEditReset && (
              <Button
                size="sm" variant="ghost"
                onClick={() => setEditingReset(true)}
                data-testid="incentive-reset-edit"
              >
                <Pencil className="h-3 w-3" />
              </Button>
            )}
          </>
        )}
        <span className="ml-auto text-[11px] text-amber-900">
          As of <strong>{data?.as_of}</strong>
        </span>
      </div>

      {/* Workshop-wide summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className={`border rounded-lg p-3 ${workshopTone}`} data-testid="incentive-workshop-total">
          <p className="text-[10px] uppercase tracking-wider font-bold opacity-70 flex items-center gap-1">
            <Award className="h-3 w-3" /> Workshop bucket
          </p>
          <p className="text-2xl font-black tabular-nums mt-0.5">
            {fmtSignedHours(workshopTotal)}
          </p>
        </div>
        <div className="border border-emerald-200 bg-emerald-50/60 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-800 opacity-80 flex items-center gap-1">
            <TrendingUp className="h-3 w-3" /> Positive mechanics
          </p>
          <p className="text-2xl font-black tabular-nums text-emerald-800 mt-0.5" data-testid="incentive-positive-count">
            {data?.positive_bucket_count || 0}
            <span className="text-xs font-medium text-emerald-700 ml-1">
              / {data?.mechanic_count || 0}
            </span>
          </p>
        </div>
        <div className="border border-rose-200 bg-rose-50/60 rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wider font-bold text-rose-800 opacity-80 flex items-center gap-1">
            <TrendingDown className="h-3 w-3" /> Negative mechanics
          </p>
          <p className="text-2xl font-black tabular-nums text-rose-800 mt-0.5" data-testid="incentive-negative-count">
            {data?.negative_bucket_count || 0}
            <span className="text-xs font-medium text-rose-700 ml-1">
              / {data?.mechanic_count || 0}
            </span>
          </p>
        </div>
      </div>

      {/* Per-mechanic table */}
      {(!data?.mechanics || data.mechanics.length === 0) ? (
        <p className="text-sm text-slate-500 py-4 text-center">
          No mechanics on file yet.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="incentive-mechanic-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-3">#</th>
                <th className="py-2 pr-3">Mechanic</th>
                <th className="py-2 px-3 text-right">Daily target</th>
                <th className="py-2 px-3 text-right">Completed (period)</th>
                <th className="py-2 px-3 text-right">Bucket balance</th>
              </tr>
            </thead>
            <tbody>
              {data.mechanics.map((m, i) => {
                const bucket = Number(m.bucket_total_hours || 0);
                const tone =
                  bucket > 0.05 ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                  : bucket < -0.05 ? 'text-rose-700 bg-rose-50 border-rose-200'
                  : 'text-slate-700 bg-slate-50 border-slate-200';
                return (
                  <tr
                    key={m.user_id}
                    className="border-b border-slate-100 hover:bg-slate-50/60"
                    data-testid={`incentive-row-${m.user_id}`}
                  >
                    <td className="py-2.5 pr-3 text-slate-400 tabular-nums">{i + 1}</td>
                    <td className="py-2.5 pr-3">
                      <p className="font-medium text-slate-800">{m.name}</p>
                      <p className="text-[11px] text-slate-500 uppercase tracking-wider">{m.role}</p>
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-600">
                      {Number(m.daily_target_hours || 0).toFixed(1)}h
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono text-slate-800">
                      {Number(m.completed_hours_total || 0).toFixed(1)}h
                      <span className="text-[10px] text-slate-400 block leading-none mt-0.5">
                        of {Number(m.target_hours_total || 0).toFixed(0)}h target
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-black tabular-nums border ${tone}`}
                        data-testid={`incentive-bucket-value-${m.user_id}`}
                      >
                        {bucket > 0.05 ? <TrendingUp className="h-3 w-3" /> : bucket < -0.05 ? <TrendingDown className="h-3 w-3" /> : null}
                        {fmtSignedHours(bucket)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[11px] text-slate-500 mt-3 flex items-center gap-1">
        <Users className="h-3 w-3" />
        Set each mechanic&apos;s daily target in <strong>User Management → Daily hours</strong>.
        Weekly target = daily × working days.
      </p>
    </Card>
  );
}
